import React from "react";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHeading, Overline, PillButton } from "@/components/primitives";
import { Icon } from "@/lib/icons";
import { useSeo } from "@/lib/useSeo";
import { COMPANY, VALUES, IMAGES } from "@/lib/content";

const STORY = [
  "Vardhix Technologies was created to solve real technology and operational gaps in Indian digital businesses.",
  "The company identified a clear need for better trading evaluation software and technology built specifically for the Indian market.",
  "The company began by building and operating Capital Tree Funding — a technology-enabled trading evaluation platform.",
  "It quickly expanded its technology, user base, operational systems, and product vision.",
  "Today, Vardhix aims to create multiple technology brands under one parent company.",
  "The long-term goal is to build a diversified technology ecosystem serving businesses and users across India.",
];

const INCORP = [
  ["Legal Name", COMPANY.legalName],
  ["Corporate Identification Number", COMPANY.cin],
  ["Registered Office", COMPANY.registeredOffice],
  ["Corporate Office", COMPANY.corporateOffice],
  ["Company Type", COMPANY.companyType],
  ["Industry", COMPANY.industry],
  ["Email", COMPANY.email],
  ["Website", COMPANY.website],
];

const WHY = [
  "India-focused product development",
  "Deep understanding of digital platform operations",
  "Strong fintech and trading technology capabilities",
  "Scalable infrastructure",
  "Automated risk and compliance controls",
  "Fast product iteration",
  "User-focused support systems",
  "Multi-brand operating capability",
];

export default function About() {
  useSeo({
    title: "About Us",
    description:
      "Vardhix Technologies Private Limited is an Indian technology company focused on technology development, software infrastructure, digital platforms, fintech solutions, and business automation.",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "About", path: "/about" }],
  });

  return (
    <>
      <PageHero
        overline="About Us"
        title="An Indian technology company built for scale"
        sub="Vardhix Technologies Private Limited focuses on technology development, software infrastructure, digital platforms, fintech solutions, and business automation — operating technology-driven brands including Capital Tree Funding."
      />

      {/* Overview + image */}
      <section className="relative py-16 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border border-white/10">
              <img src={IMAGES.office} alt="Vardhix Technologies corporate environment" className="w-full object-cover aspect-[4/3]" />
            </div>
          </Reveal>
          <div>
            <SectionHeading overline="Company Overview" title="Technology, infrastructure and digital platforms" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-slate-300 leading-relaxed">
                {COMPANY.legalName} is an Indian private limited company focused on technology
                development, software infrastructure, digital platforms, fintech solutions, and
                business automation. The company operates and manages technology-driven brands,
                including Capital Tree Funding, while continuing to develop additional products for
                Indian businesses and users.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="relative py-24 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading overline="Our Story" title="From one platform to a technology ecosystem" />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STORY.map((s, i) => (
              <Reveal key={i} delay={(i % 3) * 0.07}>
                <div className="glass rounded-2xl p-7 h-full">
                  <span className="text-teal font-heading font-bold text-2xl">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-4 text-slate-300 leading-relaxed">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Incorporation details */}
      <section className="relative py-24 border-t border-white/10">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <SectionHeading center overline="Incorporation Details" title="Company information" />
          <Reveal delay={0.1}>
            <div className="mt-12 glass rounded-3xl overflow-hidden">
              {INCORP.map(([k, v], i) => (
                <div key={k} className={`grid sm:grid-cols-3 gap-2 px-7 py-5 ${i !== INCORP.length - 1 ? "border-b border-white/10" : ""}`}>
                  <span className="text-sm text-slate-500">{k}</span>
                  <span className="sm:col-span-2 text-sm text-white">{v}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-24 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading overline="Values" title="What we stand for" />
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 4) * 0.06}>
                <div className="glass rounded-2xl p-6 h-full hover:border-teal/40 transition-colors">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                    <Icon name={v.icon} className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-heading font-semibold text-white">{v.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Vardhix */}
      <section className="relative py-24 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHeading overline="Why Vardhix" title="Why partner with us" />
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {WHY.map((w, i) => (
              <Reveal key={w} delay={(i % 2) * 0.06}>
                <div className="flex items-start gap-3 glass rounded-xl px-5 py-4">
                  <Icon name="Check" className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-200">{w}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 border-t border-white/10">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <Overline>Work With Us</Overline>
          <h2 className="mt-5 text-3xl md:text-4xl font-heading font-semibold text-white">Let&rsquo;s build something enduring</h2>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <PillButton to="/contact" testid="about-contact-cta">Contact Our Team</PillButton>
            <PillButton to="/platforms" variant="secondary" testid="about-platforms-cta" icon="ArrowUpRight">Explore Our Platforms</PillButton>
          </div>
        </div>
      </section>
    </>
  );
}
